import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  LinearProgress,
  Typography,
  Stack
} from '@mui/material'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import SignalCellularOffIcon from '@mui/icons-material/SignalCellularOff'
import ErrorIcon from '@mui/icons-material/Error'
import { useNetworkStatus } from '../hooks/useNetworkStatus'
import { useSocketConnection } from '../hooks/useSocketConnection'

interface DisconnectionWarningProps {
  open: boolean
  onClose: () => void
  reason: 'network-offline' | 'socket-disconnected' | 'socket-timeout'
  retryCount?: number
  maxRetries?: number
}

export const DisconnectionWarning: React.FC<DisconnectionWarningProps> = ({
  open,
  onClose,
  reason,
  retryCount = 0,
  maxRetries = 3
}) => {
  const networkStatus = useNetworkStatus()
  const { reconnectSocket } = useSocketConnection()
  const [autoRetrying, setAutoRetrying] = useState(false)
  const [retryIn, setRetryIn] = useState(5)

  useEffect(() => {
    if (!autoRetrying) return

    const timer = setInterval(() => {
      setRetryIn((prev) => {
        if (prev <= 1) {
          handleRetry()
          return 5
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [autoRetrying])

  const handleRetry = () => {
    reconnectSocket()
    setRetryIn(5)
    setAutoRetrying(false)
  }

  const getDialogContent = () => {
    switch (reason) {
      case 'network-offline':
        return {
          icon: <CloudOffIcon sx={{ fontSize: 48, color: '#d32f2f' }} />,
          title: 'No Internet Connection',
          message: 'Your device has lost internet connectivity. Please check your connection and try again.',
          actionText: 'Retry'
        }
      case 'socket-disconnected':
        return {
          icon: <SignalCellularOffIcon sx={{ fontSize: 48, color: '#d32f2f' }} />,
          title: 'Disconnected from Server',
          message: `Connection to the server was lost. ${retryCount < maxRetries ? `Retrying... (${retryCount}/${maxRetries})` : 'Please try reconnecting.'}`,
          actionText: 'Reconnect'
        }
      case 'socket-timeout':
        return {
          icon: <ErrorIcon sx={{ fontSize: 48, color: '#f57c00' }} />,
          title: 'Connection Timeout',
          message: 'The connection to the server timed out. This may be due to network issues or server overload.',
          actionText: 'Try Again'
        }
      default:
        return {
          icon: <SignalCellularOffIcon sx={{ fontSize: 48, color: '#d32f2f' }} />,
          title: 'Connection Error',
          message: 'An error occurred with your connection. Please try again.',
          actionText: 'Retry'
        }
    }
  }

  const content = getDialogContent()
  const canRetry = reason !== 'network-offline' || networkStatus.isOnline

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          {content.icon}
        </Box>
        {content.title}
      </DialogTitle>

      <DialogContent sx={{ pt: 2, textAlign: 'center' }}>
        <Stack spacing={2}>
          <Typography variant="body1" color="textSecondary">
            {content.message}
          </Typography>

          {reason === 'socket-disconnected' && retryCount > 0 && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" color="textSecondary">
                Retry Attempt {retryCount} of {maxRetries}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(retryCount / maxRetries) * 100}
                sx={{ mt: 1 }}
              />
            </Box>
          )}

          {autoRetrying && (
            <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="body2" color="primary">
                Auto-retrying in {retryIn} seconds...
              </Typography>
              <LinearProgress sx={{ mt: 1 }} />
            </Box>
          )}

          {reason !== 'network-offline' && (
            <Typography variant="caption" color="textSecondary">
              Connection Type: {networkStatus.connectionType} | Network Status:{' '}
              {networkStatus.isOnline ? '🟢 Online' : '🔴 Offline'}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2 }}>
        {reason !== 'network-offline' && (
          <Button
            onClick={() => {
              setAutoRetrying(!autoRetrying)
            }}
            variant="outlined"
            disabled={!canRetry}
          >
            {autoRetrying ? 'Stop Auto-Retry' : 'Auto-Retry'}
          </Button>
        )}
        <Button
          onClick={handleRetry}
          variant="contained"
          disabled={!canRetry || autoRetrying}
          color="primary"
        >
          {content.actionText}
        </Button>
        {reason !== 'network-offline' && (
          <Button onClick={onClose} variant="text">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
